import React, { useEffect, useRef, useState } from "react";
import { FiUpload, FiFile, FiX, FiPlay } from "react-icons/fi";
import { GoCheck, GoSync } from "react-icons/go";

/**
 * Dropzone de XMLs: aceita .xml soltos ou um .zip com os XMLs do período.
 * - `onFilesChange` recebe os File reais (para upload ao backend).
 * - Se `actionLabel`/`onAction` forem passados, exibe o botão de ação.
 */
export default function XmlDropzone({ titulo, hint, actionLabel, onAction, onFilesChange, forwardRef }) {
  const [arquivos, setArquivos] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  const aceitos = arquivos.filter((f) => f.status === "accepted");

  useEffect(() => {
    onFilesChange?.(aceitos.map((f) => f.file));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arquivos]);

  const addFiles = (fileList) => {
    const novos = Array.from(fileList).map((f) => {
      const ok = /\.(xml|zip)$/i.test(f.name);
      return {
        id: Date.now() + Math.random(),
        name: f.name,
        size: f.size,
        file: f,
        status: ok ? "accepted" : "rejected",
        reason: ok ? (f.name.toLowerCase().endsWith(".zip") ? "ZIP aceito" : "XML aceito") : "Formato inválido (.xml ou .zip)",
      };
    });
    if (novos.length) setArquivos((prev) => [...prev, ...novos]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const executar = () => {
    onAction?.(aceitos.map((f) => f.file));
    setArquivos([]);
  };

  return (
    <div className="dropzone-wrap" ref={forwardRef}>
      <div
        className={`dropzone ${isDragging ? "dragging" : ""}`}
        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); if (e.currentTarget === e.target) setIsDragging(false); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <div className="dz-icon"><FiUpload /></div>
        <div className="dz-text">
          <h4>{isDragging ? "Solte os XMLs aqui" : titulo || "Arraste os XMLs aqui"}</h4>
          <p>{hint || "ou clique para selecionar · .xml soltos ou um .zip do período"}</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".xml,.zip"
          style={{ display: "none" }}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {arquivos.length > 0 && (
        <div className="file-list">
          {arquivos.map((f) => (
            <div key={f.id} className={`file-item ${f.status}`}>
              <span className="fi-icon">
                {f.status === "accepted" ? <GoCheck /> : f.status === "validating" ? <GoSync className="spin" /> : <FiX />}
              </span>
              <span className="fi-info">
                <span className="fi-name"><FiFile /> {f.name}</span>
                <span className="fi-meta">{(f.size / 1024).toFixed(1)} KB · {f.reason}</span>
              </span>
              <button
                type="button"
                className="fi-remove"
                onClick={() => setArquivos((prev) => prev.filter((x) => x.id !== f.id))}
                aria-label="Remover arquivo"
              >
                <FiX />
              </button>
            </div>
          ))}
          <div className="file-list-footer">
            <span>{aceitos.length} XML(s) válido(s)</span>
            {actionLabel && (
              <button
                type="button"
                className="btn-primary"
                disabled={aceitos.length === 0}
                onClick={executar}
              >
                <FiPlay /> {actionLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
